import * as React from 'react';
import { FormSectionBase, FormSectionBaseState } from '../Types/FormSectionBase';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { ApplicationType, FieldName, FormType } from '../Types/Intyg.enums';
import { RefSetter } from '../Utils/RefSetter';
import { AntalIntygPerSprak, IntygPerSprakModel } from './AntalIntygPerSprak';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { LeveransAdress } from '../Types/ansokan.types';
import { MAX_LENGTH_NUM } from '../Services/intyg.validation.service';

export interface IManuellaAdressuppgifterProps {
  id: number;
}

export interface IManuellaAdressuppgifterState extends FormSectionBaseState {}

export class ManuellaAdressuppgifter extends FormSectionBase<
  IManuellaAdressuppgifterProps,
  IManuellaAdressuppgifterState
> {
  private antalIntygRef: React.RefObject<AntalIntygPerSprak>;
  constructor(props: IManuellaAdressuppgifterProps) {
    super(props);
    const { id } = this.props;
    this.antalIntygRef = React.createRef<AntalIntygPerSprak>();
    this.fieldRefs
      .set(`${FieldName.Adressrad1}_${id}`, React.createRef<TextInput>())
      .set(`${FieldName.Adressrad2}_${id}`, React.createRef<TextInput>())
      .set(`${FieldName.Adressrad3}_${id}`, React.createRef<TextInput>())
      .set(`${FieldName.Adressrad4}_${id}`, React.createRef<TextInput>())
      .set(`${FieldName.Adressrad5}_${id}`, React.createRef<TextInput>());

    this.state = {
      errors: this.emptyErrorsMap,
    };
  }

  public hasErrors = () => {
    // Check children
    const antalIntygErrors = this.antalIntygRef?.current?.hasErrors();
    const myErrors = super.hasErrors();
    return antalIntygErrors || myErrors;
  };

  get intygPerSprakModel(): IntygPerSprakModel[] {
    const { model, formType } = this.context;
    const arr: IntygPerSprakModel[] = [];

    if (!this.model) return arr;

    const pushSwedish = () => {
      arr.push({
        fieldName: FieldName.AntalSvenska,
        label: 'Svenska',
        value: this.model.antalSvenska,
      });
    };
    const pushEnglish = () => {
      arr.push({
        fieldName: FieldName.AntalEngelska,
        label: 'Engelska',
        value: this.model.antalEngelska,
      });
    };

    switch (formType) {
      case FormType.Intyg_for_aktuell_legitimation:
      case FormType.Intyg_om_AT_tjanstgoring:
        pushEnglish();
        break;
      case FormType.Artikel_33:
      case FormType.Intyg_for_legitimation_inom_EU:
        if (model.pdfSvenska) pushSwedish();
        if (model.pdfEngelska) pushEnglish();
        break;
      case FormType.Intyg_om_forskrivningsratt:
      case FormType.Intyg_om_kontaktlinsbehorighet:
        pushSwedish();
        break;
    }

    return arr;
  }

  get model(): LeveransAdress {
    return this.context.model.adresser?.find((x) => x.id === this.props.id);
  }

  getUpdatedAdressObject = (fieldName: string, value: any) => {
    const leveransAdressArrClone = this.context.model.adresser.slice();
    const leveransAdressClone = leveransAdressArrClone.find((x) => x.id === this.props.id);
    if (!leveransAdressClone) return console.error('Could not find a leveransadress with given id: ', this.props.id);
    leveransAdressClone[fieldName] = value;
    return leveransAdressArrClone;
  };

  textInputChanged = (field: FormField<string>) => {
    const { updateModelUsingKeyValue } = this.context;

    this.onChangeField(field, (field) => {
      if (this.context.applicationType == ApplicationType.Eservice) {
        updateModelUsingKeyValue(
          'adresser',
          this.getUpdatedAdressObject(field.fieldName.replace(/_\d+$/, ''), field.value)
        );
      }
    });
  };

  public render() {
    const { id } = this.props;
    const { Translate, formLanguage } = this.context;
    return (
      <RefSetter fieldRefs={this.fieldRefs}>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              id={`${id}_${FieldName.Adressrad1}`}
              label={`${Translate('organisation')}`}
              fieldName={`${FieldName.Adressrad1}_${id}`}
              isRequired={false}
              onChange={this.textInputChanged}
              errorMessage={this.getErrorMessage(`${FieldName.Adressrad1}_${id}`)}
              value={this.model?.adressrad1}
              autoComplete="organization"
              key={`${FieldName.Adressrad1}_${id}`}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              id={`${id}_${FieldName.Adressrad2}`}
              label={`${Translate('mottagarnamn')} (obligatoriskt)`}
              fieldName={`${FieldName.Adressrad2}_${id}`}
              isRequired={true}
              onChange={this.textInputChanged}
              errorMessage={this.getErrorMessage(`${FieldName.Adressrad2}_${id}`)}
              value={this.model?.adressrad2}
              autoComplete="name"
              key={`${FieldName.Adressrad2}_${id}`}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              id={`${id}_${FieldName.Adressrad3}`}
              label={`${Translate('adress1')} (obligatoriskt)`}
              fieldName={`${FieldName.Adressrad3}_${id}`}
              isRequired={true}
              onChange={this.textInputChanged}
              errorMessage={this.getErrorMessage(`${FieldName.Adressrad3}_${id}`)}
              value={this.model?.adressrad3}
              autoComplete="address-line1"
              key={`${FieldName.Adressrad3}_${id}`}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              id={`${id}_${FieldName.Adressrad4}`}
              label={`${Translate('adress2')}`}
              fieldName={`${FieldName.Adressrad4}_${id}`}
              isRequired={false}
              onChange={this.textInputChanged}
              errorMessage={this.getErrorMessage(`${FieldName.Adressrad4}_${id}`)}
              value={this.model?.adressrad4}
              autoComplete="address-line2"
              key={`${FieldName.Adressrad4}_${id}`}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
            />
          </div>
        </div>
        <div className="row m-b-2">
          <div className="col-sm-6">
            <TextInput
              id={`${id}_${FieldName.Adressrad5}`}
              label={`${Translate('land')} (obligatoriskt)`}
              fieldName={`${FieldName.Adressrad5}_${id}`}
              isRequired={true}
              onChange={this.textInputChanged}
              errorMessage={this.getErrorMessage(`${FieldName.Adressrad5}_${id}`)}
              value={this.model?.adressrad5}
              autoComplete="country-name"
              key={`${FieldName.Adressrad5}_${id}`}
              maxLength={MAX_LENGTH_NUM}
              showMaxLengthMessage={true}
              maxLengthMessageLanguage={formLanguage}
            />
          </div>
        </div>

        <AntalIntygPerSprak ref={this.antalIntygRef} id={id} intygPerSprakModels={this.intygPerSprakModel} />
      </RefSetter>
    );
  }
}
